import { uploadData } from 'aws-amplify/storage';

/** Matches document grid rotate menu values. */
export type ImageRotateKind = 'cw90' | 'ccw90' | 'deg180';

const LICENSE_READER_BUCKET = 'licenseReader';

const RASTER_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp'
]);

function extensionForMime(mime: string): string {
  if (mime === 'image/png') return '.png';
  if (mime === 'image/webp') return '.webp';
  return '.jpg';
}

function outputMimeFromSource(sourceMime: string): 'image/jpeg' | 'image/png' | 'image/webp' {
  if (sourceMime === 'image/png') return 'image/png';
  if (sourceMime === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

/**
 * Maps browser / S3 content types to a value suitable for Amplify `uploadData` options.
 */
export function amplifyImageContentType(mime: string): string {
  const m = (mime || '').toLowerCase();
  if (m === 'image/jpg' || m === 'image/jpeg') return 'image/jpeg';
  if (m === 'image/png') return 'image/png';
  if (m === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

/**
 * Key for Amplify `uploadData` / `downloadData`: trimmed, no `?`/`#`, no leading `/`.
 * If the list API returns a full `http(s)` URL, uses `URL.pathname` as the object key.
 */
export function normalizeStorageKeyFromApi(raw: string): string {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) {
    try {
      const u = new URL(v);
      return u.pathname.replace(/^\/+/, '').split('?')[0].split('#')[0] || '';
    } catch {
      return '';
    }
  }
  return v.replace(/^\/+/, '').split('?')[0].split('#')[0];
}

export function isRasterImageMime(mime: string): boolean {
  return RASTER_MIME.has((mime || '').toLowerCase());
}

/** Uses path extension when `blobType` is missing or generic. */
export function isRasterImageForBrowserRotation(
  objectPath: string,
  blobType?: string
): boolean {
  const ext = objectPath.split('.').pop()?.toLowerCase() || '';
  const extRaster =
    ext === 'jpg' ||
    ext === 'jpeg' ||
    ext === 'png' ||
    ext === 'webp';
  if (blobType && isRasterImageMime(blobType)) return true;
  return extRaster;
}

function rotationRadians(kind: ImageRotateKind): number {
  switch (kind) {
    case 'cw90':
      return Math.PI / 2;
    case 'ccw90':
      return -Math.PI / 2;
    case 'deg180':
      return Math.PI;
    default:
      return 0;
  }
}

/**
 * Decodes a raster `Blob`, rotates it in a canvas, and returns a new encoded blob (no extra npm deps).
 * Prefer JPEG for non-PNG inputs to limit size; PNG stays PNG; WebP stays WebP when the browser supports encoding.
 */
export async function rotateRasterImageBlob(
  source: Blob,
  kind: ImageRotateKind
): Promise<{ blob: Blob; contentType: string }> {
  if (!source?.size) {
    throw new Error('Empty image blob');
  }
  const sourceMime = (source.type || 'image/jpeg').toLowerCase();
  if (!isRasterImageMime(sourceMime)) {
    throw new Error('Only JPEG, PNG, or WebP can be rotated in the browser');
  }

  const bitmap = await createImageBitmap(source);
  try {
    const w = bitmap.width;
    const h = bitmap.height;
    const swap = kind === 'cw90' || kind === 'ccw90';
    const canvas = document.createElement('canvas');
    canvas.width = swap ? h : w;
    canvas.height = swap ? w : h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rotationRadians(kind));
    ctx.drawImage(bitmap, -w / 2, -h / 2);

    const outMime = outputMimeFromSource(sourceMime);
    const quality = outMime === 'image/jpeg' ? 0.92 : undefined;

    const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), outMime, quality);
    });
    if (!blob?.size) {
      throw new Error('Failed to encode rotated image');
    }
    return { blob, contentType: outMime };
  } finally {
    bitmap.close();
  }
}

export type UploadRotatedImageToLicenseReaderParams = {
  path: string;
  blob: Blob;
  contentType: string;
  metadata?: Record<string, string>;
  bucket?: string;
};

/**
 * Uploads bytes to S3 via Amplify, same pattern as `uploadLicenseModal` / `uploadData`.
 */
export async function uploadRotatedRasterToLicenseReader(
  params: UploadRotatedImageToLicenseReaderParams
): Promise<{ path: string }> {
  const { path, blob, contentType, metadata, bucket = LICENSE_READER_BUCKET } =
    params;
  const ct = amplifyImageContentType(contentType);
  const res = await uploadData({
    path,
    data: blob,
    options: {
      contentType: ct,
      metadata,
      bucket
    }
  }).result;
  return { path: res.path };
}

/**
 * Rotates a raster blob and uploads the result to the license reader bucket.
 */
export async function rotateRasterBlobAndUploadToLicenseReader(options: {
  sourceBlob: Blob;
  kind: ImageRotateKind;
  uploadPath: string;
  metadata?: Record<string, string>;
  bucket?: string;
}): Promise<{ path: string; contentType: string }> {
  const { blob, contentType } = await rotateRasterImageBlob(
    options.sourceBlob,
    options.kind
  );
  const { path } = await uploadRotatedRasterToLicenseReader({
    path: options.uploadPath,
    blob,
    contentType,
    metadata: options.metadata,
    bucket: options.bucket
  });
  return { path, contentType };
}

/**
 * Rotates then uploads to `pathPrefix/fileName` where `fileName` is derived from the **output** MIME.
 */
export async function rotateRasterBlobAndUploadWithAutoName(options: {
  sourceBlob: Blob;
  kind: ImageRotateKind;
  /** e.g. `${licenseKey}/${documentId}` — no trailing slash */
  pathPrefix: string;
  originalPathOrName: string;
  metadata?: Record<string, string>;
  bucket?: string;
}): Promise<{ path: string; contentType: string }> {
  const { blob, contentType } = await rotateRasterImageBlob(
    options.sourceBlob,
    options.kind
  );
  const fileName = rotatedUploadFileName(
    options.originalPathOrName,
    contentType
  );
  const prefix = options.pathPrefix.replace(/\/+$/, '');
  const { path } = await uploadRotatedRasterToLicenseReader({
    path: `${prefix}/${fileName}`,
    blob,
    contentType,
    metadata: options.metadata,
    bucket: options.bucket
  });
  return { path, contentType };
}

/**
 * Builds a stable file name for the rotated upload (keeps extension aligned with encoded MIME).
 */
export function rotatedUploadFileName(originalPathOrName: string, contentType: string): string {
  const base =
    originalPathOrName.split('/').pop()?.trim() || 'document';
  const stem = base.includes('.')
    ? base.slice(0, base.lastIndexOf('.'))
    : base;
  const safeStem = stem.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 120) || 'rotated';
  return `${safeStem}_rotated${extensionForMime(contentType)}`;
}
